// GIS data for the Longmen-Yumenkou flood emergency storyline.
// The scene is set at the real geographic location of 龙门镇禹门口
// where the Yellow River exits the Longmen Gorge between Shaanxi and Shanxi.
// All coordinates are WGS84 [lng, lat] pairs based on the actual river course,
// town layout, and floodplain topography of the area.
//
// ── 真实地理参照 ──
// 禹门口：黄河出晋陕峡谷的咽喉，位于陕西韩城龙门镇与山西河津市之间
// 龙门镇：陕西省韩城市龙门镇，位于黄河西岸台地
// 下峪口：龙门镇南侧沿河社区
// 侯西铁路禹门口黄河大桥：连接陕晋两省的铁路桥
// 108国道：沿黄河西岸南北向穿过龙门镇
// 黄河在此段由北向北偏西→南偏东流向，河宽约300-400m

export const LONGMEN_GIS_DATA = {
  camera: {
    // 垂直于河道斜视：相机置于洪水中心 ENE 侧，朝 WSW 方向俯瞰
    // 黄河在此段南北流向，相机从东岸向西岸斜视，正对洪水演进中心
    lng: 110.582,
    lat: 35.615,
    alt: 5500,
    heading: -2.0,    // ≈ -115°，朝 WSW 方向（垂直于南北向河道）
    pitch: -0.9599,   // -55° 斜视
  },

  // ── 洪水演进动画配置 ──
  floodAnimation: {
    enabled: true,
    centerLng: 110.555,
    centerLat: 35.605,
    heightMin: 378,
    heightMax: 388,
    stretchX: 1.6,
    stretchY: 2.8,
    timeline: {
      time: ['0h', '1h', '2h', '3h', '4h', '5h', '6h', '8h', '10h', '12h'],
      area: [1.2, 3.5, 6.5, 9.5, 12.0, 11.0, 8.5, 5.0, 2.5, 1.0],
    },
  },

  releaseCorridor: {
    id: 'release-corridor-longmen-yumenkou',
    name: '龙门镇禹门口下泄影响走廊',
    color: '#38f8ff',
    warning: '承接古贤枢纽洪水预演下泄方案，聚焦禹门口、龙门镇、下峪口和黄河滩区的本地联动断面',
    description: '自古贤坝下排漂设施起，沿黄河主槽经龙门水文站、禹门口大桥、龙门镇沿河带、下峪口社区，至黄河滩区施工营地，全长约 7.2 km',
    lengthKm: 7.2,
    positions: [
      [110.538, 35.645],
      [110.542, 35.632],
      [110.550, 35.620],
      [110.553, 35.605],
      [110.560, 35.590],
    ],
    pointLabels: [
      { name: '古贤坝下排漂设施', type: 'origin' },
      { name: '龙门水文站', type: 'station' },
      { name: '禹门口大桥断面', type: 'section' },
      { name: '龙门镇复核断面', type: 'section' },
      { name: '下峪口滩区末端', type: 'endpoint' },
    ],
  },

  responseSections: [
    {
      id: 'sec-yumenkou-bridge',
      name: '禹门口大桥封控断面',
      lng: 110.550,
      lat: 35.620,
      level: '红色管控',
      action: 'T+0 封控禹门口大桥，停止陕晋两岸非应急车辆通行',
      station: '禹门口大桥监测点',
      warningLevel: '385.50 m',
      designLevel: '388.00 m',
      responseTime: 'T+0 min',
    },
    {
      id: 'sec-longmen-town',
      name: '龙门镇水情复核断面',
      lng: 110.553,
      lat: 35.605,
      level: '橙色复核',
      action: 'T+3 复核上游来水、龙门镇水位涨率，确认广播发布状态',
      station: '龙门水文站',
      warningLevel: '382.20 m',
      designLevel: '384.50 m',
      responseTime: 'T+3 min',
    },
    {
      id: 'sec-xiayukou',
      name: '下峪口社区预警断面',
      lng: 110.557,
      lat: 35.593,
      level: '橙色封控',
      action: 'T+5 完成下峪口沿河居民区清场和路线守控',
      station: '下峪口水位桩',
      warningLevel: '381.00 m',
      designLevel: '383.20 m',
      responseTime: 'T+5 min',
    },
  ],

  riskPoints: [
    {
      id: 'risk-yumenkou-shops',
      name: '禹门口桥头商铺带',
      type: '橙区经营点',
      zone: 'orange',
      lng: 110.551,
      lat: 35.618,
      people: 420,
      action: '桥头封控，商户和游客沿 L2 路线向禹门口观景台撤离',
      contact: '禹门口社区网格员',
      priority: '第一序列',
      exceedance: 0.5,
    },
    {
      id: 'risk-longmen-riverside',
      name: '龙门镇沿河居民区',
      type: '红区居民点',
      zone: 'red',
      lng: 110.554,
      lat: 35.608,
      people: 1260,
      action: '强制离开，只出不进，敲门组清最后一排住户',
      contact: '龙门镇防汛值守室',
      priority: '第一序列',
      exceedance: 0.8,
    },
    {
      id: 'risk-xiayukou-village',
      name: '下峪口沿河住户区',
      type: '红区居民点',
      zone: 'red',
      lng: 110.558,
      lat: 35.592,
      people: 680,
      action: '强制撤离，沿 L3 路线向龙门镇第二小学转移',
      contact: '下峪口社区主任',
      priority: '第一序列',
      exceedance: 0.7,
    },
    {
      id: 'risk-construction-camp',
      name: '黄河滩区施工营地',
      type: '临时工棚',
      zone: 'red',
      lng: 110.563,
      lat: 35.586,
      people: 310,
      action: '施工班组点名，关闭低位便道，转移至龙门镇应急集结点',
      contact: '施工总包安全员',
      priority: '第一序列',
      exceedance: 1.1,
    },
    {
      id: 'risk-farmland',
      name: '黄河滩区农田作业带',
      type: '滩区作业面',
      zone: 'yellow',
      lng: 110.568,
      lat: 35.582,
      people: 1190,
      action: '停止作业，农机离开滩区，人员向高台方向撤离',
      contact: '村组联络员',
      priority: '第二序列',
      exceedance: 0.3,
    },
  ],

  shelters: [
    {
      id: 'shelter-longmen-primary',
      name: '龙门镇第一小学',
      level: '一级避险点',
      lng: 110.544,
      lat: 35.612,
      capacity: 1600,
      manager: '王敏（龙门镇第一小学校长）',
      supplies: '饮水 1200 人份、急救包 24 套、照明 4 套、帐篷 20 顶',
      register: '按村组二维码和纸质表双登记',
      polygon: [
        [110.5430, 35.6110],
        [110.5452, 35.6110],
        [110.5452, 35.6132],
        [110.5430, 35.6132],
      ],
    },
    {
      id: 'shelter-yumenkou-viewing',
      name: '禹门口观景平台',
      level: '一级避险点',
      lng: 110.547,
      lat: 35.624,
      capacity: 900,
      manager: '禹门口景区管理处',
      supplies: '雨衣 500 件、饮水 800 人份、对讲机 6 部、急救包 12 套',
      register: '商户、游客、居民分区登记',
      polygon: [
        [110.5460, 35.6228],
        [110.5482, 35.6228],
        [110.5482, 35.6252],
        [110.5460, 35.6252],
      ],
    },
    {
      id: 'shelter-longmen-middle',
      name: '龙门镇第二小学（初中部）',
      level: '一级避险点',
      lng: 110.540,
      lat: 35.605,
      capacity: 1200,
      manager: '李建国（龙门镇第二小学校长）',
      supplies: '安全帽 350 顶、照明 3 套、担架 4 副、折叠床 200 张',
      register: '施工班组与居民分区点名制',
      polygon: [
        [110.5388, 35.6038],
        [110.5412, 35.6038],
        [110.5412, 35.6062],
        [110.5388, 35.6062],
      ],
    },
    {
      id: 'shelter-cultural-center',
      name: '龙门镇文化体育广场',
      level: '二级综合安置点',
      lng: 110.542,
      lat: 35.616,
      capacity: 2800,
      manager: '龙门镇民政专干',
      supplies: '食品 4200 人份、折叠床 800 张、棉被 1200 条、医疗站 1 座',
      register: '6-24 小时安置和物资发放',
      polygon: [
        [110.5405, 35.6148],
        [110.5438, 35.6148],
        [110.5438, 35.6175],
        [110.5405, 35.6175],
      ],
    },
    {
      id: 'shelter-beiyuan-plateau',
      name: '北塬台地临时安置点',
      level: '临时避险点',
      lng: 110.535,
      lat: 35.620,
      capacity: 500,
      manager: '龙门镇武装部',
      supplies: '应急照明 2 套、对讲机 3 部、帐篷 50 顶',
      register: '村组登记制',
      polygon: [
        [110.5335, 35.6185],
        [110.5368, 35.6185],
        [110.5368, 35.6218],
        [110.5335, 35.6218],
      ],
    },
  ],

  materialPoints: [
    {
      id: 'mat-longmen-warehouse',
      name: '龙门镇防汛物资仓库',
      lng: 110.546,
      lat: 35.614,
      material: '砂袋 5200 条、救生衣 760 件、铁锹 200 把',
      target: '桥头围挡、沿河居民区和滩区作业人员',
      manager: '赵磊',
      polygon: [
        [110.5452, 35.6132],
        [110.5470, 35.6132],
        [110.5470, 35.6150],
        [110.5452, 35.6150],
      ],
    },
    {
      id: 'mat-town-government',
      name: '龙门镇政府大院',
      lng: 110.548,
      lat: 35.610,
      material: '应急发电机 8 台、移动照明灯组 12 套、对讲机 30 部',
      target: '广播、避险点照明、通信中继',
      manager: '王敏',
      polygon: [
        [110.5470, 35.6090],
        [110.5492, 35.6090],
        [110.5492, 35.6110],
        [110.5470, 35.6110],
      ],
    },
    {
      id: 'mat-river-rescue',
      name: '黄河岸线抢险停靠点',
      lng: 110.556,
      lat: 35.598,
      material: '冲锋舟 4 艘、抛绳器 8 套、救生圈 60 个',
      target: '低洼孤点接驳、滩区被困人员搜救',
      manager: '李建国',
      polygon: [
        [110.5550, 35.5970],
        [110.5572, 35.5970],
        [110.5572, 35.5992],
        [110.5550, 35.5992],
      ],
    },
    {
      id: 'mat-secondary-shelter',
      name: '龙门镇二级安置物资仓',
      lng: 110.541,
      lat: 35.617,
      material: '饮用水和方便食品 4200 人份、棉被 800 条',
      target: '龙门镇文化体育广场（二级安置点）',
      manager: '赵磊',
      polygon: [
        [110.5400, 35.6162],
        [110.5420, 35.6162],
        [110.5420, 35.6178],
        [110.5400, 35.6178],
      ],
    },
  ],

  routes: [
    {
      id: 'route-l1',
      name: 'L1 龙门镇沿河居民区→第一小学路线',
      color: '#38f8ff',
      start: '龙门镇沿河居民区红区入口',
      end: '龙门镇第一小学',
      walkMinutes: 7,
      capacity: 1600,
      guard: '路线引导员A、交通封控与引导组',
      distanceKm: 1.5,
      positions: [
        [110.554, 35.608],
        [110.551, 35.610],
        [110.548, 35.611],
        [110.544, 35.612],
      ],
    },
    {
      id: 'route-l2',
      name: 'L2 禹门口桥头商铺→观景平台路线',
      color: '#facc15',
      start: '禹门口桥头商铺带',
      end: '禹门口观景平台',
      walkMinutes: 5,
      capacity: 900,
      guard: '路线引导员B、禹门口社区网格员',
      distanceKm: 0.8,
      positions: [
        [110.551, 35.618],
        [110.550, 35.620],
        [110.548, 35.622],
        [110.547, 35.624],
      ],
    },
    {
      id: 'route-l3',
      name: 'L3 下峪口住户→第二小学路线',
      color: '#40ffba',
      start: '下峪口沿河住户区',
      end: '龙门镇第二小学（初中部）',
      walkMinutes: 8,
      capacity: 1200,
      guard: '下峪口社区主任、交通封控与引导组',
      distanceKm: 2.0,
      positions: [
        [110.558, 35.592],
        [110.555, 35.596],
        [110.550, 35.600],
        [110.545, 35.603],
        [110.540, 35.605],
      ],
    },
    {
      id: 'route-l4',
      name: 'L4 滩区施工营地→应急集结点路线',
      color: '#ff8a3d',
      start: '黄河滩区施工营地',
      end: '龙门镇文化体育广场',
      walkMinutes: 10,
      capacity: 700,
      guard: '施工安全员、枢纽工程抢险队',
      distanceKm: 2.5,
      positions: [
        [110.563, 35.586],
        [110.558, 35.592],
        [110.552, 35.600],
        [110.546, 35.610],
        [110.542, 35.616],
      ],
    },
  ],

  zones: [
    {
      id: 'zone-red-riverside',
      name: '红区：禹门口-龙门镇沿河禁留带',
      color: '#ff4d4f',
      rule: '黄河主槽近岸 0-50 m、禹门口大桥上下游 100 m、龙门镇首排临河房屋和滩区临时工棚',
      action: '强制离开，不设观察点、不堆物资、不停车',
      positions: [
        [110.549, 35.624],
        [110.556, 35.614],
        [110.560, 35.600],
        [110.564, 35.590],
        [110.570, 35.584],
        [110.572, 35.590],
        [110.562, 35.602],
        [110.556, 35.612],
        [110.552, 35.622],
      ],
    },
    {
      id: 'zone-orange-buffer',
      name: '橙区：龙门镇缓冲撤离区',
      color: '#ff8a3d',
      rule: '龙门镇临河二排房屋、下峪口社区沿街商铺、禹门口桥头低洼带和短时滞留点',
      action: '警报后 7 分钟内到一级避险点',
      positions: [
        [110.548, 35.614],
        [110.556, 35.610],
        [110.560, 35.602],
        [110.562, 35.594],
        [110.556, 35.590],
        [110.552, 35.594],
        [110.548, 35.600],
        [110.545, 35.608],
      ],
    },
    {
      id: 'zone-yellow-farmland',
      name: '黄区：黄河滩区作业撤离带',
      color: '#f8ff59',
      rule: '滩区农田作业面、低位道路和农机临停点，只用于穿越撤离',
      action: '不长期停留，到一级避险点剩余步行不超过 4 分钟',
      positions: [
        [110.560, 35.584],
        [110.574, 35.580],
        [110.576, 35.588],
        [110.572, 35.592],
        [110.566, 35.590],
      ],
    },
    {
      id: 'zone-blue-shelter',
      name: '蓝区：龙门镇高地避险安置区',
      color: '#25d9ff',
      rule: '一级避险点（第一小学、第二小学、观景平台）、二级安置点（文化体育广场）和物资仓',
      action: '登记、分区安置、物资发放、医疗照护',
      positions: [
        [110.533, 35.602],
        [110.549, 35.608],
        [110.549, 35.626],
        [110.533, 35.626],
      ],
    },
  ],

  metadata: {
    sceneName: '龙门镇禹门口沿河片区',
    riverName: '黄河',
    corridorCount: 1,
    responseSectionCount: 3,
    riskPointCount: 5,
    shelterCount: 5,
    materialPointCount: 4,
    routeCount: 4,
    zoneCount: 4,
    totalEvacuationPeople: 3860,
    totalShelterCapacity: 7000,
  },
}
